import type { ErrorCode } from "@scalephant/domain";

import { ErrorResponseSchema } from "../schemas/errors.js";

/**
 * The single HTTP transport for the SPA, used by every orval-generated hook.
 * Transplanted from Scalephant 1.0 `custom-fetch.ts`, reduced to what a browser
 * SPA behind the edge needs: same-origin cookies, JSON in/out, one error type.
 */

const NO_BODY_STATUS = new Set([204, 205, 304]);

let baseUrl: string | null = null;

/** Prepends a base URL to relative request paths. Only needed outside the browser (tests, tooling). */
export function setBaseUrl(url: string | null): void {
  baseUrl = url ? url.replace(/\/+$/, "") : null;
}

interface RequestInfo {
  readonly method: string;
  readonly url: string;
}

export class ApiError extends Error {
  override readonly name = "ApiError";
  readonly status: number;
  /** Code from the API error envelope, or null when the response was not one. */
  readonly code: ErrorCode | null;
  /** From the envelope, else from X-Request-Id (the edge and api both set it). */
  readonly correlationId: string | null;
  readonly details: Readonly<Record<string, unknown>> | undefined;
  readonly data: unknown;
  readonly headers: Headers;
  /** Parsed Retry-After in seconds when the server sent one (rate limits). */
  readonly retryAfterSeconds: number | null;
  readonly method: string;
  readonly url: string;

  constructor(status: number, data: unknown, headers: Headers, request: RequestInfo) {
    const envelope = ErrorResponseSchema.safeParse(data);
    super(
      envelope.success
        ? envelope.data.error.message
        : `HTTP ${status} from ${request.method} ${request.url}`,
    );
    this.status = status;
    this.code = envelope.success ? envelope.data.error.code : null;
    this.correlationId = envelope.success
      ? envelope.data.error.correlationId
      : headers.get("x-request-id");
    this.details = envelope.success ? envelope.data.error.details : undefined;
    this.data = data;
    this.headers = headers;
    this.retryAfterSeconds = parseRetryAfter(headers.get("retry-after"));
    this.method = request.method;
    this.url = request.url;
  }
}

function parseRetryAfter(value: string | null): number | null {
  if (value === null) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : Math.max(0, Math.ceil((at - Date.now()) / 1000));
}

export class ResponseParseError extends Error {
  override readonly name = "ResponseParseError";
  readonly status: number;
  readonly rawBody: string;

  constructor(status: number, rawBody: string, request: RequestInfo, cause: unknown) {
    super(`Failed to parse response from ${request.method} ${request.url} (${status}) as JSON`, {
      cause,
    });
    this.status = status;
    this.rawBody = rawBody;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

// orval picks these up from the mutator module: every generated hook's error is an ApiError.
export type ErrorType<_Body = unknown> = ApiError;
export type BodyType<Body> = Body;

function isJsonMediaType(headers: Headers): boolean {
  const mediaType = headers.get("content-type")?.split(";", 1)[0]?.trim().toLowerCase();
  return mediaType === "application/json" || (mediaType?.endsWith("+json") ?? false);
}

function looksLikeJson(text: string): boolean {
  const trimmed = text.trimStart();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

async function parseBody(response: Response, request: RequestInfo): Promise<unknown> {
  if (request.method === "HEAD" || NO_BODY_STATUS.has(response.status)) return null;
  const raw = await response.text();
  const text = raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw;
  if (text.trim() === "") return null;
  if (isJsonMediaType(response.headers) || looksLikeJson(text)) {
    try {
      return JSON.parse(text) as unknown;
    } catch (cause) {
      // An unparseable error body is still an error response; keep the text.
      if (!response.ok) return raw;
      throw new ResponseParseError(response.status, raw, request, cause);
    }
  }
  return raw;
}

export async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  // Generated hooks JSON.stringify their bodies; only label those, never arbitrary text.
  if (
    typeof options.body === "string" &&
    !headers.has("content-type") &&
    looksLikeJson(options.body)
  ) {
    headers.set("content-type", "application/json");
  }
  if (!headers.has("accept")) headers.set("accept", "application/json");

  const target = baseUrl !== null && url.startsWith("/") ? `${baseUrl}${url}` : url;
  const request: RequestInfo = { method, url: target };

  const response = await fetch(target, {
    ...options,
    method,
    headers,
    credentials: options.credentials ?? "same-origin",
  });
  const body = await parseBody(response, request);
  if (!response.ok) throw new ApiError(response.status, body, response.headers, request);
  return body as T;
}

/**
 * Sends a file straight to the private bucket through a URL the api presigned (SP-022).
 *
 * The one request the SPA makes that is not to the api: the bytes never pass through it.
 * The signed headers are sent exactly as granted, no cookies go along, and a refusal is an
 * ApiError like any other so the caller handles one kind of failure.
 */
export async function putPresignedUpload(
  url: string,
  headers: Readonly<Record<string, string>>,
  body: Blob,
): Promise<void> {
  const request: RequestInfo = { method: "PUT", url };
  // The browser sets Content-Length itself from the body and refuses to have it set.
  const sent = Object.fromEntries(
    Object.entries(headers).filter(([name]) => name.toLowerCase() !== "content-length"),
  );
  const response = await fetch(url, { method: "PUT", headers: sent, body, credentials: "omit" });
  if (!response.ok) {
    throw new ApiError(response.status, await response.text(), response.headers, request);
  }
}
